import Table from "./Table"
import TableToolbar from "./TableToolbar"
import TableContent from "./TableContent"
import TableSearch from "./TableSearch"
import TableCount from "./TableCount"
import TableGroupSelector from "./TableGroupSelector"
import TableColumnButton from "./TableColumnButton"
import TableSelectionToolbar from "./TableSelectionToolbar"
import TableRowToolbar from "./TableRowToolbar"
import TableRowActions from "./TableRowActions"
import TableEmpty from "./TableEmpty"
import TableToolbarMenu from "./TableToolbarMenu"
import TableToolbarFilters from "./TableToolbarFilters"
import TableGroupList from "./TableGroupList"
import TableFilterButton from "./filter/TableFilterButton"
import TableFilterList from "./filter/TableFilterList"
import TableStatusFilter from "./TableStatusFilter"
import TableExportSelectionButton from "./export/TableExportSelectionButton"
import TablePagination from "./TablePagination"

export { useTableContext } from "./table.context"
export {
  createColumns,
  getColumnTitle,
  getDefaultColumnVisibility,
} from "./table.utils"

export { paginatedField, useTableQuery } from "./table.apollo"

export { summaryFraction } from "./summary/table.summary.utils"

export { useTableSortUrl } from "./table.sortUrl"
export type { TableSortUrlProps } from "./table.sortUrl"

export type { UseTableQueryOptions, UseTableQueryResult } from "./table.apollo"
export type { TableProps } from "./Table"
export type { TableToolbarProps } from "./TableToolbar"
export type { TableContentProps } from "./TableContent"
export type { TableSearchProps } from "./TableSearch"
export type { TableCountProps } from "./TableCount"
export type { TableGroupSelectorProps } from "./TableGroupSelector"
export type { TableColumnButtonProps } from "./TableColumnButton"
export type { TableSelectionToolbarProps } from "./TableSelectionToolbar"
export type { TableRowToolbarProps } from "./TableRowToolbar"
export type { TableRowActionsProps } from "./TableRowActions"
export type { TableEmptyProps } from "./TableEmpty"
export type { TableToolbarMenuProps } from "./TableToolbarMenu"
export type { TableGroupListProps } from "./TableGroupList"
export type { TableFilterButtonProps } from "./filter/TableFilterButton"
export type { TableFilterListProps } from "./filter/TableFilterList"
export type {
  TableStatusFilterProps,
  TableStatusFilterOptionProps,
} from "./TableStatusFilter"
export type { TableExportSelectionButtonProps } from "./export/TableExportSelectionButton"
export type { TablePaginationProps } from "./TablePagination"
export type { TableBodyProps } from "./TableBody"
export type { TableBodyRowProps } from "./TableBodyRow"
export type {
  ColumnMeta,
  ColumnMetaMap,
  ColumnFilterType,
  ColumnFilterConfig,
  ColumnFilterOption,
  GroupingRange,
  TableQueryFilter,
  TableQuerySort,
  PageInfo,
  TableQueryParams,
  FilterGroup,
} from "./table.types"
export type { DateFilterValue, DateFilterMode } from "./filter/dateFilter"

export { isDateFilterValue } from "./filter/dateFilter"
export { GROUPING_RANGE_DATES } from "./table.grouping"

/**
 * Compound table component built on @tanstack/react-table.
 *
 * @example
 * ```tsx
 * <Table data={customers} columns={COLUMNS} manualSorting manualFiltering
 *   onQueryChange={(params) => setInput({ limit: 100, offset: 0, ...params })}
 * >
 *   <Table.Toolbar>
 *     <Table.FilterButton />
 *     <Table.ColumnButton />
 *     <Table.GroupSelector />
 *     <Table.Search column="Name" placeholder="Search..." />
 *     <Table.Count label="items" pageInfo={pageInfo} />
 *   </Table.Toolbar>
 *   <Table.FilterList />
 *   <Table.Content isLoading={loading} error={error} pageInfo={pageInfo} onFetchMore={...} />
 *   <Table.RowToolbar column="Name">
 *     {(customer) => <Button>{customer.Name}</Button>}
 *   </Table.RowToolbar>
 *   <Table.SelectionToolbar>
 *     {(selectedRows, reset) => <Button>Bulk action</Button>}
 *   </Table.SelectionToolbar>
 * </Table>
 * ```
 */
export default Object.assign(Table, {
  Toolbar: TableToolbar,
  Content: TableContent,
  Search: TableSearch,
  Count: TableCount,
  GroupSelector: TableGroupSelector,
  ColumnButton: TableColumnButton,
  ToolbarMenu: TableToolbarMenu,
  ToolbarFilters: TableToolbarFilters,
  FilterButton: TableFilterButton,
  FilterList: TableFilterList,
  GroupList: TableGroupList,
  SelectionToolbar: TableSelectionToolbar,
  ExportSelectionButton: TableExportSelectionButton,
  RowToolbar: TableRowToolbar,
  StatusFilter: TableStatusFilter,
  RowActions: TableRowActions,
  Empty: TableEmpty,
  Pagination: TablePagination,
})
